// The interface's typeface, bundled rather than fetched. See src/leads/ui.js.
import '@fontsource-variable/public-sans'
import React from 'react'
import { createRoot } from 'react-dom/client'
/*
  THE STAFF APP IS FETCHED ONLY BY STAFF (walk-through, 2026-10-08). Every renter's phone used to
  download the whole back office before it could draw one home. The app arrives in its own file,
  only when this page turns out to be the app.

  AND SINCE MTR #622, SO DOES EVERY RENTER'S PAGE. Nine of them were in one 997 kB file, and her
  phone had to download and run all nine before it could draw one home. Measured on a throttled
  phone — 9 Mbps, 70 ms away, a quarter of a laptop's processor — opening the same /homes against
  the same answers:

      one file for everything ..... 1,006 kB   first paint 1,840 ms   first home 2,358 ms
      a file for each page .......     405 kB   first paint 1,040 ms   first home 1,572 ms

  The split belongs here because this is where the page is already decided from the address: the
  file that arrives is the one this address needs, and no other. The enquiry form's phone field
  alone — the flags of every country in the world, and Google's phone-number rules — is 437 kB that
  the search page never draws.

  WHAT THIS DOES NOT FIX, so nobody expects it to. `react-dom` is 186 kB of the 405 that are left,
  and a page that draws one word with it still paints at 540 ms on the same phone; the first screen
  arriving already drawn in the HTML paints at 144 ms. Splitting takes 800 ms off. The 500 after
  that is a different change, and #622 says so as well.
*/
const AppLive = React.lazy(() => import('./src/data/AppLive.jsx').then((m) => ({ default: m.AppLive })))
const PublicQuote = React.lazy(() => import('./src/quote/PublicQuote.jsx').then((m) => ({ default: m.PublicQuote })))
import { createPublicQuoteApi } from './src/data/publicQuoteApi.js'
import { tokenFromPath } from './src/quote/publicLink.js'
const RenterLease = React.lazy(() => import('./src/lease/RenterLease.jsx').then((m) => ({ default: m.RenterLease })))
import { createRenterLeaseApi } from './src/lease/renterLeaseApi.js'
import { leaseTokenFromPath } from './src/lease/leasePath.js'
const CleanerPage = React.lazy(() => import('./src/property/CleanerPage.jsx').then((m) => ({ default: m.CleanerPage })))
import { createCleanApi } from './src/property/cleanApi.js'
import { cleanTokenFromPath, moveInTokenFromPath } from './src/property/cleanPath.js'
const MoveInPage = React.lazy(() => import('./src/property/MoveInPage.jsx').then((m) => ({ default: m.MoveInPage })))
import { createMoveInApi } from './src/property/moveInApi.js'
const PublicEnquiry = React.lazy(() => import('./src/intake/PublicEnquiry.jsx').then((m) => ({ default: m.PublicEnquiry })))
import { createPublicEnquiryApi } from './src/data/publicEnquiryApi.js'
import { slugFromPath } from './src/listing/enquiryLink.js'
const PublicStay = React.lazy(() => import('./src/listing/PublicStay.jsx').then((m) => ({ default: m.PublicStay })))
import { createPublicListingApi } from './src/data/publicListingApi.js'
import { stayKeyFromPath, ambassadorFromPath } from './src/listing/publicHome.js'
const ChatPage = React.lazy(() => import('./src/chat/ChatPage.jsx').then((m) => ({ default: m.ChatPage })))
/* The address is read here, so the page it names cannot be what tells us how to read it. */
import { chatTokenFromPath } from './src/chat/chatPath.js'
const PublicBrowse = React.lazy(() => import('./src/listing/PublicBrowse.jsx').then((m) => ({ default: m.PublicBrowse })))
import { stayFromQuery, companyDayOf } from './src/listing/browseFilters.js'
import { renterRedirect, platformAddressRedirect } from './src/shared/renterPaths.js'
import { shouldLoadAgain } from './src/shared/reloadOnce.js'
const PublicTour = React.lazy(() => import('./src/links/PublicTour.jsx').then((m) => ({ default: m.PublicTour })))
import { tourCodeFromPath } from './src/links/shortLink.js'
const PublicMedia = React.lazy(() => import('./src/links/PublicMedia.jsx').then((m) => ({ default: m.PublicMedia })))
import { mediaCodeFromPath } from './src/links/mediaLink.js'

/**
 * Two products share one bundle, and the split happens HERE — before either of
 * them is constructed.
 *
 * `/q/<token>` is the renter's page. It has no session, loads no portfolio, and
 * must never render a line of code that assumes somebody is signed in. Deciding
 * that at the root, rather than inside `AppLive`, is what makes the guarantee
 * structural instead of careful: on that path `AppLive` is not mounted at all,
 * so there is no ordering to get wrong later and no sign-in form that can flash
 * at a stranger holding a link we sent her.
 *
 * It is `location.pathname` and not a router. This product has three addressable
 * pages; a router would be a dependency whose entire job is to answer this one
 * question, and `server.mjs` already sends every unknown path to this file.
 *
 * `/home/<slug>` is the third, added for #88: the enquiry form the Product Owner
 * locked on 2026-09-26, on a page a renter can open. It is decided here for the
 * same structural reason as the quote — on this path `AppLive` is not mounted at
 * all — and it is checked SECOND, because a slug and a token cannot collide and
 * the order should still be the one somebody reading this expects.
 */
/*
  A FILE THAT IS NO LONGER THERE LOADS THE PAGE AGAIN, ONCE. Now that her page arrives in a second
  file, a deploy between this file being served and that file being asked for leaves her asking for a
  name that has just changed. Vite raises `vite:preloadError` for it; `shouldLoadAgain` decides, and
  says there why it is once per tab and never without a memory of having tried.
*/
globalThis.addEventListener?.('vite:preloadError', () => {
  let box = null
  try { box = globalThis.sessionStorage ?? null } catch { return }
  if (shouldLoadAgain(box)) globalThis.location?.reload()
})

const path = globalThis.location?.pathname
const token = tokenFromPath(path)
const slug = token ? null : slugFromPath(path)
/* MTR #88 — a home's public page, /stay/<words>-<key>. Answered by the listing server. */
const stayKey = token || slug ? null : stayKeyFromPath(path)
/* Ambassador Program — /stay/<words>-<key>/<name>: the same home, opened from somebody's link. */
const ambassador = stayKey ? (ambassadorFromPath(path)?.code ?? null) : null
/* MTR #223 — her own conversation, /chat/<token>. Answered by the same server as the listing. */
const chatToken = token || slug || stayKey ? null : chatTokenFromPath(path)
/* Project doc 105 — every home a renter may open, as one search page: /homes. */
const browsing = !token && !slug && !stayKey && !chatToken && /^\/homes\/?$/.test(path ?? '')
/* MTR #509 — our own tour page, /tour/<code>, which a short link we send opens. */
const tourCode = !token && !slug && !stayKey && !chatToken && !browsing ? tourCodeFromPath(path) : null
/* FB-0023 — the photos and videos we sent her, /media/<code>, which a short link we send opens. */
const mediaCode = !token && !slug && !stayKey && !chatToken && !browsing && !tourCode ? mediaCodeFromPath(path) : null
/* MTR #578 — her lease, /lease/<token>: read, initial and sign. Answered by the same server as the listing. */
const leaseToken = !token && !slug && !stayKey && !chatToken && !browsing && !tourCode ? leaseTokenFromPath(path) : null
/* MTR #599 part 2 — a cleaner's photo link, /clean/<token>. Answered by the same server as the listing. */
const cleanToken = !token && !slug && !stayKey && !chatToken && !browsing && !tourCode && !leaseToken ? cleanTokenFromPath(path) : null
/* MTR #599 part 3 — her move-in check, /move-in/<token>. */
const moveInToken = !token && !slug && !stayKey && !chatToken && !browsing && !tourCode && !leaseToken && !cleanToken ? moveInTokenFromPath(path) : null
const newId = () => globalThis.crypto?.randomUUID?.() ?? `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
const storage = (() => { try { return globalThis.localStorage ?? null } catch { return null } })()
const LISTING_SERVER = import.meta.env?.VITE_MTR_SERVER_URL ?? null
const URL = import.meta.env?.VITE_SUPABASE_URL
const ANON = import.meta.env?.VITE_SUPABASE_ANON_KEY

/*
  ONE SUBMISSION ID PER PAGE LOAD, MINTED HERE RATHER THAN INSIDE THE FORM.

  It is what makes a retried submit find the same answer instead of writing a second one, so it has
  to outlive a failed attempt and must not outlive the page. Minting it in the component would give
  a re-render a new one; minting it per session would give her second enquiry the id of her first,
  which is exactly the defect Copilot found on mtr#85.
*/
const submissionId = `link:${slug}:${globalThis.crypto?.randomUUID?.() ?? Date.now()}`

/*
  THE WEBSITE'S FUNNEL (migration 0075): on the renter's pages, who sent her (an ambassador's name) and
  where from (the site, or the campaign she came with) go with every step she takes. Only the host and
  the campaign name are ever kept, on the server; the page she landed on is counted once per visit.
*/
/*
  WHERE SHE CAME FROM IS WHERE SHE LANDED (review of #481). Going from /homes to a home is a new page
  load whose referrer is our own site, so the landing page's referrer, campaign and ambassador are kept
  for the rest of the visit (this tab) and sent with every step. A device a colleague has signed in on
  is not counted at all: staff looking at the site are not renters.
*/
const signedInHere = (() => { try { return Object.keys(globalThis.localStorage ?? {}).some((k) => /^sb-.*-auth-token$/.test(k)) } catch { return false } })()
/*
  A RENTER'S PAGE OPENED ON my.stayoo.com MOVES TO go.stayoo.com (MTR #509, project doc 113). Every link
  already sent — quotes, forms, an ambassador's home link — keeps working this way. A colleague signed in
  on this device stays: her view is ours. Off until the renters' address is switched on.
*/
/* The platform's own address (<service>.up.railway.app) moves to my.stayoo.com first (renterPaths.js). */
const offOurAddress = globalThis.location ? platformAddressRedirect({
  origin: globalThis.location.origin, path, search: globalThis.location.search, hash: globalThis.location.hash,
}) : null
const movedTo = offOurAddress ?? (globalThis.location ? renterRedirect({
  origin: globalThis.location.origin, path, search: globalThis.location.search, hash: globalThis.location.hash, signedIn: signedInHere,
}) : null)
if (movedTo) globalThis.location.replace(movedTo)
const trackWith = signedInHere ? null : (() => {
  const here = {
    ref: ambassador,
    referrer: globalThis.document?.referrer || null,
    utm: (() => { try { return new URLSearchParams(globalThis.location?.search ?? '').get('utm_source') } catch { return null } })(),
  }
  try {
    const kept = JSON.parse(globalThis.sessionStorage?.getItem('stayoo.from') ?? 'null')
    if (kept) return { ref: kept.ref ?? here.ref, referrer: kept.referrer, utm: kept.utm }
    globalThis.sessionStorage?.setItem('stayoo.from', JSON.stringify(here))
  } catch { /* this page's own answer */ }
  return here
})()
const renterApi = () => {
  const api = createPublicListingApi({ baseUrl: LISTING_SERVER, trackWith })
  if (!trackWith) return api
  try {
    if (!globalThis.sessionStorage?.getItem('stayoo.visit')) {
      globalThis.sessionStorage?.setItem('stayoo.visit', '1')
      api.visit()
    }
  } catch { api.visit() }
  return api
}

/* The clock is read ONCE, here, and handed down. No component in this product reads it. */
const NOW = new Date().toISOString()

/*
  ONE WAIT FOR ALL OF THEM, AND IT DRAWS NOTHING WHILE IT WAITS. The page she asked for is the only
  one being fetched, so there is nothing truthful to show in its place: a spinner for a third of a
  second is a flash, and a skeleton of the wrong page is a lie.
*/
if (!movedTo) createRoot(document.getElementById('mtr-root')).render(
  <React.Suspense fallback={null}>{
  token && URL && ANON
    ? <PublicQuote api={createPublicQuoteApi({ url: URL, anonKey: ANON })} token={token} />
    : slug && URL && ANON
      ? <PublicEnquiry
        api={createPublicEnquiryApi({ url: URL, anonKey: ANON })}
        slug={slug} now={NOW} submissionId={submissionId}
      />
      : stayKey && LISTING_SERVER
        ? <PublicStay api={renterApi()} stayKey={stayKey} now={NOW}
          formId={globalThis.crypto?.randomUUID?.() ?? `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`}
          newId={newId} storage={storage}
          /* The page, not its query string: ad parameters (utm, fbclid) are not ours to keep (review of #223). */
          pageUrl={globalThis.location ? `${globalThis.location.origin}${globalThis.location.pathname}` : null}
          ambassador={ambassador}
          /* The dates of the search she came from (project doc 105). */
          initialStay={stayFromQuery(globalThis.location?.search, companyDayOf(NOW))}
          /* Where she came from — only its host is ever kept, on the server (listing/ambassador.js). */
          referrer={globalThis.document?.referrer || null} />
        : chatToken && LISTING_SERVER
          ? <ChatPage api={createPublicListingApi({ baseUrl: LISTING_SERVER })} token={chatToken} newId={newId} storage={storage} />
          : browsing && LISTING_SERVER
            ? <PublicBrowse api={renterApi()} now={NOW} storage={storage} />
            : tourCode && LISTING_SERVER
              ? <PublicTour api={createPublicListingApi({ baseUrl: LISTING_SERVER })} code={tourCode} />
            : mediaCode && LISTING_SERVER
              ? <PublicMedia api={createPublicListingApi({ baseUrl: LISTING_SERVER })} code={mediaCode} />
            : leaseToken && LISTING_SERVER
              ? <RenterLease api={createRenterLeaseApi({ baseUrl: LISTING_SERVER })} token={leaseToken} now={NOW} />
            : cleanToken && LISTING_SERVER
              ? <CleanerPage api={createCleanApi({ baseUrl: LISTING_SERVER })} token={cleanToken} />
            : moveInToken && LISTING_SERVER
              ? <MoveInPage api={createMoveInApi({ baseUrl: LISTING_SERVER })} token={moveInToken} />
            : <AppLive />
  }</React.Suspense>
)
